import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import {
  dialogFormActionsSx,
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx
} from '../utils/dialogFormLayout';

const TASK_TYPES = ['Reward', 'Punishment', 'Neutral'];
const RATINGS = ['Light', 'Normal', 'Extreme', 'Hardcore'];
const ITEM_STATES = ['None', 'Available', 'Equipped', 'Any'];
const COMMENT_TYPES = [
  'Contract',
  'ItemAppear',
  'RewardAppear',
  'PlayerCondition',
  'SupportProject'
];
const FORM_ADDRESSES = ['M', 'F', 'Any'];

function emptyTaskState() {
  return {
    englishTaskBlocks: { preparation: '', task: '', completion: '' },
    russianTaskBlocks: { preparation: '', task: '', completion: '' },
    english: '',
    russian: '',
    requiredFetishes: [],
    requiredItems: [],
    newStateItems: [],
    taskForm: '',
    taskType: 'Neutral',
    rating: 'Normal',
    foreplay: false,
    duration: 0,
    count: 0,
    score: 0,
    improved: false
  };
}

function emptyCommentState() {
  return {
    type: 'Contract',
    english: '',
    russian: '',
    requiredFetishes: [],
    requiredItems: [],
    preferredFormAddress: 'Any'
  };
}

function emptyDisobeyState() {
  return { english: '', russian: '', repeatNumber: 0 };
}

function emptyItemPairState() {
  return { item1: '', item2: '' };
}

function normalizeTask(item) {
  const base = emptyTaskState();
  if (!item) return base;
  return {
    ...base,
    ...item,
    englishTaskBlocks: { ...base.englishTaskBlocks, ...(item.englishTaskBlocks || {}) },
    russianTaskBlocks: { ...base.russianTaskBlocks, ...(item.russianTaskBlocks || {}) },
    requiredFetishes: Array.isArray(item.requiredFetishes) ? item.requiredFetishes.map(String) : [],
    requiredItems: Array.isArray(item.requiredItems)
      ? item.requiredItems.map((x) => ({
          item: String(x.item || ''),
          state: x.state || 'Available'
        }))
      : [],
    newStateItems: Array.isArray(item.newStateItems)
      ? item.newStateItems.map((x) => ({
          item: String(x.item || ''),
          state: x.state || 'Available'
        }))
      : []
  };
}

function normalizeComment(item) {
  const base = emptyCommentState();
  if (!item) return base;
  return {
    ...base,
    ...item,
    requiredFetishes: Array.isArray(item.requiredFetishes) ? item.requiredFetishes.map(String) : [],
    requiredItems: Array.isArray(item.requiredItems) ? item.requiredItems.map(String) : []
  };
}

function normalizeDisobey(item) {
  const base = emptyDisobeyState();
  if (!item) return base;
  return { ...base, ...item, repeatNumber: Number(item.repeatNumber) || 0 };
}

function normalizeItemPair(item) {
  const base = emptyItemPairState();
  if (!item) return base;
  return {
    item1: String(item.item1 || ''),
    item2: String(item.item2 || '')
  };
}

const JoiItemForm = ({
  open,
  onClose,
  onSubmit,
  tab,
  item = null,
  kinks = [],
  realItems = []
}) => {
  const isEdit = Boolean(item && item._id);

  const [taskState, setTaskState] = useState(emptyTaskState);
  const [commentState, setCommentState] = useState(emptyCommentState);
  const [disobeyState, setDisobeyState] = useState(emptyDisobeyState);
  const [itemPairState, setItemPairState] = useState(emptyItemPairState);

  useEffect(() => {
    if (!open) return;
    if (tab === 'tasks') setTaskState(normalizeTask(item));
    else if (tab === 'comments') setCommentState(normalizeComment(item));
    else if (tab === 'disobeys') setDisobeyState(normalizeDisobey(item));
    else if (tab === 'item-pairs') setItemPairState(normalizeItemPair(item));
  }, [open, tab, item]);

  const kinkOptions = useMemo(
    () => kinks.map((k) => ({ id: String(k._id), label: k.name || String(k._id) })),
    [kinks]
  );
  const realItemOptions = useMemo(
    () => realItems.map((r) => ({ id: String(r._id), label: r.name || String(r._id) })),
    [realItems]
  );

  const kinkById = useMemo(() => {
    const m = new Map();
    for (const o of kinkOptions) m.set(o.id, o);
    return m;
  }, [kinkOptions]);

  const realItemById = useMemo(() => {
    const m = new Map();
    for (const o of realItemOptions) m.set(o.id, o);
    return m;
  }, [realItemOptions]);

  const resolveKinkValues = (ids) =>
    (ids || []).map((id) => kinkById.get(String(id)) || { id: String(id), label: String(id) });

  const resolveRealItemValues = (ids) =>
    (ids || []).map(
      (id) => realItemById.get(String(id)) || { id: String(id), label: String(id) }
    );

  const handleSubmit = (e) => {
    e.preventDefault();
    let payload;
    if (tab === 'tasks') payload = taskState;
    else if (tab === 'comments') payload = commentState;
    else if (tab === 'disobeys') payload = disobeyState;
    else if (tab === 'item-pairs') payload = itemPairState;
    else return;
    onSubmit(payload);
  };

  const renderTaskBlocks = (lang) => {
    const key = lang === 'en' ? 'englishTaskBlocks' : 'russianTaskBlocks';
    const block = taskState[key];
    const setBlock = (field, value) =>
      setTaskState((prev) => ({
        ...prev,
        [key]: { ...prev[key], [field]: value }
      }));
    return (
      <Stack spacing={1.5}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          {lang === 'en' ? 'English' : 'Russian'}
        </Typography>
        <TextField
          label="Preparation"
          multiline
          minRows={2}
          value={block.preparation}
          onChange={(e) => setBlock('preparation', e.target.value)}
          fullWidth
        />
        <TextField
          label="Task"
          multiline
          minRows={3}
          value={block.task}
          onChange={(e) => setBlock('task', e.target.value)}
          fullWidth
        />
        <TextField
          label="Completion"
          multiline
          minRows={2}
          value={block.completion}
          onChange={(e) => setBlock('completion', e.target.value)}
          fullWidth
        />
      </Stack>
    );
  };

  const renderItemWithStateList = (field, title) => {
    const rows = taskState[field] || [];
    const setRows = (next) => setTaskState((prev) => ({ ...prev, [field]: next }));
    return (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              setRows([...rows, { item: '', state: 'Available' }])
            }
          >
            Add
          </Button>
        </Stack>
        <Stack spacing={1}>
          {rows.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              None
            </Typography>
          )}
          {rows.map((row, idx) => (
            <Stack
              key={idx}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Autocomplete
                size="small"
                options={realItemOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={realItemById.get(String(row.item)) || (row.item ? { id: String(row.item), label: String(row.item) } : null)}
                onChange={(_, v) => {
                  const next = rows.slice();
                  next[idx] = { ...next[idx], item: v?.id || '' };
                  setRows(next);
                }}
                sx={{ flex: 1, minWidth: 200 }}
                renderInput={(params) => <TextField {...params} label="Item" />}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  value={row.state || 'Available'}
                  onChange={(e) => {
                    const next = rows.slice();
                    next[idx] = { ...next[idx], state: e.target.value };
                    setRows(next);
                  }}
                >
                  {ITEM_STATES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  const next = rows.slice();
                  next.splice(idx, 1);
                  setRows(next);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  };

  let titleText;
  if (tab === 'tasks') titleText = isEdit ? 'Edit task' : 'New task';
  else if (tab === 'comments') titleText = isEdit ? 'Edit comment' : 'New comment';
  else if (tab === 'disobeys') titleText = isEdit ? 'Edit disobey' : 'New disobey';
  else if (tab === 'item-pairs') titleText = isEdit ? 'Edit item pair' : 'New item pair';
  else titleText = '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogFormPaperSx }}
    >
      <DialogTitle sx={dialogFormTitleSx}>{titleText}</DialogTitle>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          {tab === 'tasks' && (
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={taskState.taskType}
                      onChange={(e) =>
                        setTaskState((p) => ({ ...p, taskType: e.target.value }))
                      }
                    >
                      {TASK_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Rating</InputLabel>
                    <Select
                      label="Rating"
                      value={taskState.rating}
                      onChange={(e) =>
                        setTaskState((p) => ({ ...p, rating: e.target.value }))
                      }
                    >
                      {RATINGS.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Task form"
                    value={taskState.taskForm}
                    onChange={(e) =>
                      setTaskState((p) => ({ ...p, taskForm: e.target.value }))
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    label="Count"
                    type="number"
                    value={taskState.count}
                    onChange={(e) =>
                      setTaskState((p) => ({ ...p, count: Number(e.target.value) || 0 }))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    label="Duration"
                    type="number"
                    value={taskState.duration}
                    onChange={(e) =>
                      setTaskState((p) => ({ ...p, duration: Number(e.target.value) || 0 }))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    label="Score"
                    type="number"
                    value={taskState.score}
                    onChange={(e) =>
                      setTaskState((p) => ({ ...p, score: Number(e.target.value) || 0 }))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Stack direction="column">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(taskState.foreplay)}
                          onChange={(e) =>
                            setTaskState((p) => ({ ...p, foreplay: e.target.checked }))
                          }
                        />
                      }
                      label="Foreplay"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(taskState.improved)}
                          onChange={(e) =>
                            setTaskState((p) => ({ ...p, improved: e.target.checked }))
                          }
                        />
                      }
                      label="Improved"
                    />
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderTaskBlocks('en')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTaskBlocks('ru')}
                </Grid>
              </Grid>

              <Divider />

              <Autocomplete
                multiple
                size="small"
                options={kinkOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={resolveKinkValues(taskState.requiredFetishes)}
                onChange={(_, v) =>
                  setTaskState((p) => ({
                    ...p,
                    requiredFetishes: v.map((x) => x.id)
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Required fetishes" />
                )}
              />

              {renderItemWithStateList('requiredItems', 'Required items')}
              {renderItemWithStateList('newStateItems', 'New state items')}
            </Stack>
          )}

          {tab === 'comments' && (
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={commentState.type}
                      onChange={(e) =>
                        setCommentState((p) => ({ ...p, type: e.target.value }))
                      }
                    >
                      {COMMENT_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Form address</InputLabel>
                    <Select
                      label="Form address"
                      value={commentState.preferredFormAddress}
                      onChange={(e) =>
                        setCommentState((p) => ({
                          ...p,
                          preferredFormAddress: e.target.value
                        }))
                      }
                    >
                      {FORM_ADDRESSES.map((f) => (
                        <MenuItem key={f} value={f}>
                          {f}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                label="English"
                multiline
                minRows={3}
                value={commentState.english}
                onChange={(e) =>
                  setCommentState((p) => ({ ...p, english: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Russian"
                multiline
                minRows={3}
                value={commentState.russian}
                onChange={(e) =>
                  setCommentState((p) => ({ ...p, russian: e.target.value }))
                }
                fullWidth
              />

              <Autocomplete
                multiple
                size="small"
                options={kinkOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={resolveKinkValues(commentState.requiredFetishes)}
                onChange={(_, v) =>
                  setCommentState((p) => ({
                    ...p,
                    requiredFetishes: v.map((x) => x.id)
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Required fetishes" />
                )}
              />

              <Autocomplete
                multiple
                size="small"
                options={realItemOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={resolveRealItemValues(commentState.requiredItems)}
                onChange={(_, v) =>
                  setCommentState((p) => ({
                    ...p,
                    requiredItems: v.map((x) => x.id)
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Required items" />
                )}
              />
            </Stack>
          )}

          {tab === 'disobeys' && (
            <Stack spacing={2.5}>
              <TextField
                size="small"
                label="Repeat number"
                type="number"
                value={disobeyState.repeatNumber}
                onChange={(e) =>
                  setDisobeyState((p) => ({
                    ...p,
                    repeatNumber: Number(e.target.value) || 0
                  }))
                }
                sx={{ maxWidth: 200 }}
              />
              <TextField
                label="English"
                multiline
                minRows={3}
                value={disobeyState.english}
                onChange={(e) =>
                  setDisobeyState((p) => ({ ...p, english: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Russian"
                multiline
                minRows={3}
                value={disobeyState.russian}
                onChange={(e) =>
                  setDisobeyState((p) => ({ ...p, russian: e.target.value }))
                }
                fullWidth
              />
            </Stack>
          )}

          {tab === 'item-pairs' && (
            <Stack spacing={2.5}>
              <Autocomplete
                size="small"
                options={realItemOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={
                  realItemById.get(String(itemPairState.item1)) ||
                  (itemPairState.item1
                    ? { id: String(itemPairState.item1), label: String(itemPairState.item1) }
                    : null)
                }
                onChange={(_, v) =>
                  setItemPairState((p) => ({ ...p, item1: v?.id || '' }))
                }
                renderInput={(params) => <TextField {...params} label="Item 1" />}
              />
              <Autocomplete
                size="small"
                options={realItemOptions}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={
                  realItemById.get(String(itemPairState.item2)) ||
                  (itemPairState.item2
                    ? { id: String(itemPairState.item2), label: String(itemPairState.item2) }
                    : null)
                }
                onChange={(_, v) =>
                  setItemPairState((p) => ({ ...p, item2: v?.id || '' }))
                }
                renderInput={(params) => <TextField {...params} label="Item 2" />}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default JoiItemForm;
